import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { first, map, Observable, tap } from 'rxjs';

import { DataTypesTableItem } from 'src/app/data-types/data-types-table/data-types-table.interface';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { SystemInstance } from '../../systems/models/systems.interface';
import { SystemsQuery } from '../../systems/state/systems.query';
import { DataType } from '../models/data-types.interface';
import { ConvertPiiTypeToDataTypePipe } from '../../shared/pipes/convert-pii-type-to-data-type.pipe';
import { ExtendedDataType } from 'src/app/api/models/systems/systems.interface';
import { DeepDiveResponse } from 'src/app/api/models/data-types/data-types.interface';
import { ApiClientDataTypesService } from 'src/app/api/api-client-data-types.service';
import { DataTypeSource, SuggestedStateEnum } from 'src/app/api/models/systems/systems.enum';
import { PII_TYPE_TO_DATA_TYPE_ID_MAP } from 'src/app/shared/models/pii-to-data-type-map';
import { CustomDataType } from 'src/app/api/models/company-settings/custom-data-types.interface';
import { DataTypesStore } from '../state/data-types.store';
import { DataTypesQuery } from '../state/data-types.query';
import { CustomDataTypesService } from 'src/app/company-settings/state/custom-data-types.service';
import { CustomFieldsV2Service } from 'src/app/company-settings/custom-fields-v2/custom-fields-v2.service';
import { DropdownOption } from 'src/app/shared/mine-dropdown/mine-dropdown.interface';

@Injectable({
  providedIn: 'root'
})
export class DataTypesService {

  readonly CUSTOM_DATA_TYPE_CATEGORY = 'Custom';
  
  constructor(
    private contentPipe: ContentPipe,
    private systemsQuery: SystemsQuery,
    private convertPiiTypeToDataTypePipe: ConvertPiiTypeToDataTypePipe,
    private apiDataTypesClient: ApiClientDataTypesService,
    private dataTypesStore: DataTypesStore,
    private dataTypesQuery: DataTypesQuery,
    private customDataTypesService: CustomDataTypesService,
    private customFieldsV2Service: CustomFieldsV2Service,
  ) { }

  // data types store is combination of pre-defined data types that retrived from squidex 
	// and custom data types from company settings api 
	init(): Observable<void> {
		return this.customDataTypesService.loadCustomDataTypes().pipe(
			first(),
			tap(res => this.setDataTypesStore(res)),
			tap(() => this.dataTypesStore.setLoading(false)),
			map(() => void 0),
		);
	}

  private setDataTypesStore(customDataTypes: CustomDataType[]): void {
		const cmsDataTypes = this.contentPipe.transform('data-types.dataTypes').map(f => ({...f, isCustom: false})) as DataType[];
		this.dataTypesStore.set([...cmsDataTypes, ...customDataTypes]);
	}

  selectLoading(): Observable<boolean> {
    return this.customFieldsV2Service.selectLoading().pipe(map(loading => !!loading));
  }

  getDataTypes(dataTypeIds?: string[]): DataType[] {
    const uniqueDataTypesSet = new Set<string>(dataTypeIds ? dataTypeIds.map(id => id.toLowerCase()) : []);
    const allDataTypes = this.dataTypesQuery.getAll();

    return dataTypeIds ? allDataTypes.filter(d => 
      uniqueDataTypesSet.has(d.id?.toLowerCase()) || 
      uniqueDataTypesSet.has(d.name?.toLowerCase())
    ) : allDataTypes;
  }

  selectDataTypesTableData(includeMissingTypes = true): Observable<Map<string, DataTypesTableItem>> {
    const apisToRetreiveTableData: Observable<any>[] = [
      this.systemsQuery.selectAllSystemsWithDataTypes(),
      this.systemsQuery.selectSystemsWithoutDataTypes(),
    ];

    return combineQueries(apisToRetreiveTableData).pipe(
      map(res => this.initDataTypesBySystems(res[0], res[1], includeMissingTypes)),
    );
  }

  getAcceptedDataTypes(systemInstances: SystemInstance[]): DataType[] {
    const acceptedExtendedDataTypes: ExtendedDataType[] = [];

    // Collect all accepted extended data types
    systemInstances.forEach(system => {
        system.extendedDataTypes?.forEach(dataType => {
            if (dataType.state === SuggestedStateEnum.Accepted) {
              if(dataType)
                acceptedExtendedDataTypes.push(dataType);
            }
        });
    });

    // Use getExtendedDataTypes to return unique data types with mapped source
    return this.getExtendedDataTypes(acceptedExtendedDataTypes);
}

  private initDataTypesBySystems(systemsWithDataTypes: SystemInstance[], systemsWithoutDataTypes: SystemInstance[], includeMissingTypes: boolean): Map<string, DataTypesTableItem> {
    const tableData = new Map<string, DataTypesTableItem>();
    const allDataTypes = new Map<string, DataType>();
    this.getDataTypes().forEach(d => allDataTypes.set(d.id, d));

    systemsWithDataTypes.forEach(s => {
      const dataTypesOfSystem = this.getSystemAcceptedDataTypes(s); // not a unique array
      dataTypesOfSystem.forEach(d => {
        const dataTypeId = d.source === DataTypeSource.PIIDIscovered ?
              this.convertPiiTypeToDataTypePipe.transform(d.id) : d.id;
        if (tableData.has(dataTypeId)) {
          if (!tableData.get(dataTypeId).systems.find(system => system.systemId === s.systemId)) {
            tableData.get(dataTypeId).systems.push(s); // add to systems array only if system is not exist
          }
          if (d.numOfRecords !== undefined) {
            tableData.get(dataTypeId).records += d.numOfRecords;
          }
        } else {
          tableData.set(dataTypeId, { dataType: allDataTypes.get(dataTypeId), systems: [s], records: d.numOfRecords ?? 0 });
        }
      });
    });
    
    if (includeMissingTypes && systemsWithoutDataTypes.length > 0) {
      const missing = this.contentPipe.transform('data-types.missingTypes');
      tableData.set(this.contentPipe.transform('data-types.missingTypes'), { dataType: { id: missing, name: missing, }, systems: systemsWithoutDataTypes }); // add systems with missing type
    }
    return tableData;
  }

  getSystemAcceptedDataTypes(system: SystemInstance): ExtendedDataType[] {
    return system.extendedDataTypes.filter(type => type.state === SuggestedStateEnum.Accepted);
  }

  getPiiTypeFromDataType(dataType: string): string[] {
    const piiTypes = [];

    Array.from(PII_TYPE_TO_DATA_TYPE_ID_MAP.entries()).forEach(value => {
      if (value[1] === dataType) {
        piiTypes.push(value[0]);
      }
    });

    return piiTypes;
  }

  equivalentDataTypes(dataType1: string, dataType2: string): boolean {
    // Get the PII types for both data types
    const piiTypes1 = this.getPiiTypeFromDataType(dataType1);
    const piiTypes2 = this.getPiiTypeFromDataType(dataType2);

    // Check if both data types directly map to each other in the PII map
    const mappedDataType1 = PII_TYPE_TO_DATA_TYPE_ID_MAP.get(dataType1);
    const mappedDataType2 = PII_TYPE_TO_DATA_TYPE_ID_MAP.get(dataType2);

    // Check if they are equivalent by matching PII types or direct values
    const isEquivalent =
        dataType1 === dataType2 ||                     // Direct match
        mappedDataType1 === dataType2 ||               // Mapped value of dataType1 matches dataType2
        mappedDataType2 === dataType1 ||               // Mapped value of dataType2 matches dataType1
        piiTypes1.some(type1 => piiTypes2.includes(type1));  // Overlap in mapped PII types

    return isEquivalent;
}

  // loop thorugh "extendedDataTypes" and remove duplications
  // return data types as cms representation
  getExtendedDataTypes(extendedDataTypes: ExtendedDataType[]): DataType[] {
    const dataTypeIdsSet = new Set<string>();
    extendedDataTypes.forEach(d => {
      const dataTypeId = d.source === DataTypeSource.PIIDIscovered ? this.convertPiiTypeToDataTypePipe.transform(d.id) : d.id;
      dataTypeIdsSet.add(dataTypeId);
    });
    return this.getDataTypes(Array.from(dataTypeIdsSet));
  }

  getDeepDiveData(systemId: string, dataTypes: string): Observable<DeepDiveResponse> {
    return this.apiDataTypesClient.getDeepDiveData(systemId, dataTypes);
  }

  getSuggestedDataTypes(dataTypes: ExtendedDataType[]): DataType[] {
    const extendedDataTypes = dataTypes?.filter(dataType => dataType.state === SuggestedStateEnum.Suggested);
    const hasPiiDiscoveryRecords = dataTypes?.some(dataType => dataType.state === SuggestedStateEnum.Accepted && dataType.source === DataTypeSource.PIIDIscovered && dataType.numOfRecords > 0);
    return !hasPiiDiscoveryRecords ? this.getExtendedDataTypes(extendedDataTypes) : undefined;
  }

  getDataTypesDropdownOptionsGroupedByCategory(dataTypes: DataType[], excludedDataTypeIds: string[] = []): DropdownOption[] {
    // Exclude deleted data types from the picker (they stay in the store so already-selected answers still
    // resolve their name in the table).
    const filteredDataTypes = dataTypes.filter(dataType => !dataType.isDeleted && !excludedDataTypeIds.includes(dataType.id));
    const groupedByCategory: { [category: string]: { id: string; value: string; options: { id: string; value: string }[] } } = {};

    for (const dataType of filteredDataTypes) {
        const category = dataType.category;
        const option = {
            id: dataType.id,
            value: dataType.name
        };
        if (!groupedByCategory[category]) {
            groupedByCategory[category] = {
                id: `category-${category}`,
                value: category,
                options: []
            };
        }
        groupedByCategory[category].options.push(option);
    }

    return Object.values(groupedByCategory).sort((a, b) => {
        if (a.value === this.CUSTOM_DATA_TYPE_CATEGORY) return -1;
        if (b.value === this.CUSTOM_DATA_TYPE_CATEGORY) return 1;
        return a.value?.localeCompare(b.value);
    }) as DropdownOption[];
  }

  getCmsDataTypes(): DataType[] {
    return (this.contentPipe.transform('data-types.dataTypes') ?? []) as DataType[];
  }

  getDataTypeFromPiiType(piiType: string): string | undefined {
    return PII_TYPE_TO_DATA_TYPE_ID_MAP.get(piiType);
  }
}
